/**
 * The app's ONE routing authority.
 *
 * With `output: 'export'` there is no server middleware, so auth routing runs
 * in the browser. It used to run in three places at once — AuthBoundary (root
 * landing), AdminLayout (staff gate) and useAuthSession (tenant lifecycle
 * gate) — each calling `router.replace` from its own effect. On 2026-07-29 that
 * produced an infinite loop on /admin: a stale post-login target sent a tenant
 * admin to /admin, AdminLayout sent it back to '/', and '/' read the same stale
 * target again.
 *
 * So the policy lives here as ONE pure function, `resolveRoute`, and exactly one
 * component (AuthBoundary) executes its decision. Two invariants keep it
 * loop-free, both covered by unit tests:
 *
 *   1. FIXED POINT — every path this function redirects to must itself resolve
 *      to `render` under the same state. A target that would redirect again is a
 *      bug, not a hop.
 *   2. NEVER '/' — '/' is a router, not a destination: it always redirects
 *      somewhere. Redirecting *to* it can only ever start a cycle.
 */

import type { AccessState } from '@westmont/contracts';

/** Interim staff persona (decision 2026-07-22): platform staff, not a tenant. */
export const SUPER_ADMIN_ROLE = 'super_admin';

/**
 * The only role `organization_admin` rows carry (`orgAdminRoleEnum`), and the
 * one signup verification creates. Named here so a caller that KNOWS the
 * persona (signup) can validate a target without a round trip to `/auth/me` —
 * which, on the apex, is a 401 by design.
 */
export const ORG_ADMIN_ROLE = 'org_admin';

export const LOGIN_PATH = '/login';
/** The Super Admin console root. */
export const ADMIN_PATH = '/admin';
/** Settings · User & Role — the org admin's designed landing (⚠ E04-C11). */
export const USERS_PATH = '/users';
/** The blocking "add a card" screen (⚠ E01-C34). */
export const PAYMENT_METHOD_PATH = '/billing/payment-method';
/** Business verification: upload / resubmit after a rejection (E01 US-02). */
export const DOCUMENT_SUBMISSION_PATH = '/document-submission';
/** Sign & upload the service agreement (US-02b, user 2026-07-29). */
export const SERVICE_AGREEMENT_PATH = '/service-agreement';
/** Buy a subscription after the trial lapsed (US-04 AC5 → US-05). */
export const PLANS_PATH = '/plans';
/**
 * WEST-782 — where a NON-admin tenant user waits while the org has no active
 * subscription. `/plans` is the way out of that state, but only an Org Admin
 * can buy there (Billing answers "Org Admin role required"), so parking an Org
 * User on it showed them a Setting screen their sidebar hides, full of errors.
 * This screen only tells them who can fix it.
 */
export const SUBSCRIPTION_INACTIVE_PATH = '/subscription-inactive';
/**
 * Where a tenant goes when their permission set opens no screen at all.
 *
 * ⚠ CONFIRM (E04-C11, raised 2026-08-13): ✅ E04-C11 says the landing follows
 * the permissions granted to the user, but the spec names NO fallback for a user
 * granted nothing. `/users` was that fallback and it is the bug BA reported on
 * 12/08: an org user with no grant landed on Settings · User & Role, a screen
 * their own sidebar hides. Interim answer until BA rules: a dedicated screen
 * that says so and offers sign-out, rather than a module screen they may not
 * open.
 *
 * It doubles as the "let the boundary decide" destination, which is why
 * `resolveRoute` treats it like '/': arriving here with a real landing (or a
 * lifecycle gate) redirects straight back out, so `login-form` can push it
 * before the permission set has loaded without guessing.
 */
export const NO_ACCESS_PATH = '/no-access';

/** Routes reachable without a session. Everything else needs one. */
const PUBLIC_PREFIXES = ['/login', '/signup', '/forgot-password'];

/**
 * Trailing slashes and query strings must never change a routing decision.
 * `usePathname()` already strips the query, but a hand-typed `/admin/` or a
 * static host that appends one would otherwise miss every prefix comparison.
 */
export function normalizePath(pathname: string): string {
  const raw = pathname.split(/[?#]/)[0] ?? '/';
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/** `true` when `path` is `base` itself or a route nested under it. */
function isWithin(path: string, base: string): boolean {
  return path === base || path.startsWith(`${base}/`);
}

export function isPublicRoute(pathname: string): boolean {
  const path = normalizePath(pathname);
  return PUBLIC_PREFIXES.some((prefix) => isWithin(path, prefix));
}

/** /login and /signup bounce an already-authenticated visitor into the app. */
export function isAuthLandingRoute(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === LOGIN_PATH || path === '/signup';
}

/**
 * Screens that exist ONLY while onboarding is unfinished, and that an org
 * leaves behind for good once the lifecycle gate is clear (WEST-657).
 *
 * `/document-submission` is the step US-01 AC8 sends a brand-new org to
 * (`E01_Multi-Tenant.md:134`), and US-02's persona is an org still at
 * `Pending Verification` (`E01_Multi-Tenant.md:206`). It is also a stored
 * post-login target, so a bookmark, the browser history or a stale entry can
 * put an org that finished onboarding back on it, where the screen could only
 * talk about a trial that already started. Rule 8b sends them on instead.
 *
 * Deliberately just this one route. `/service-agreement` carries a link token
 * and is reachable signed out (rule 0), and `/billing/payment-method` is a
 * screen an org returns to on purpose to change its card. Whether either
 * should close after onboarding is a separate question for BA, not this rule.
 */
export function isOnboardingStepRoute(pathname: string): boolean {
  return normalizePath(pathname) === DOCUMENT_SUBMISSION_PATH;
}

/**
 * The staff console. Matched on segment boundaries so the retired
 * `/admin-dashboard` org route is NOT mistaken for it.
 */
export function isAdminRoute(pathname: string): boolean {
  return isWithin(normalizePath(pathname), ADMIN_PATH);
}

/**
 * Where a persona goes when nothing more specific applies.
 *
 * LAST resort for a tenant, not the rule. ✅ E04-C11 says landing follows the
 * permissions granted to the user, so `RouteInput.permittedLanding` normally
 * answers this first; this only speaks when the permission set failed to load
 * or grants no module at all.
 *
 * It used to answer `/users`, and that is the whole of BA feedback 12/08 item 5:
 * `login-form` calls this DIRECTLY the moment login returns — before any
 * permission set exists — so every org user was pushed onto Settings, and
 * `AuthBoundary` never corrected it because `/users` is a normal page that just
 * renders. `NO_ACCESS_PATH` is a decision point instead: the boundary waits
 * there for the permission set and forwards to the real first module.
 *
 * `permissionsResolved` keeps that honest. `/no-access` is only a fair answer
 * once the permission set has SETTLED (loaded, or failed and said so). Before
 * it settles nobody knows what this user may open, and answering the question
 * anyway is how an org_admin ends up parked on a "no access" screen: the two
 * direct callers run before the set is even requested, and one of them
 * (`app/signup/page.tsx`) persists what it gets. `null` means "not known yet",
 * and the caller must hold the frame rather than name a destination. The
 * one-argument form keeps the old answer for those two push-once callers, whose
 * result `AuthBoundary` re-decides on the very next render.
 */
export function defaultLanding(role: string | undefined): string;
export function defaultLanding(
  role: string | undefined,
  permissionsResolved: boolean,
): string | null;
export function defaultLanding(
  role: string | undefined,
  permissionsResolved = true,
): string | null {
  if (role === SUPER_ADMIN_ROLE) return ADMIN_PATH;
  return permissionsResolved ? NO_ACCESS_PATH : null;
}

/**
 * Targets the BE is allowed to name as a post-login landing. Anything outside
 * this set is dropped rather than followed: the BE map is interim (⚠ E04-C11)
 * and a target with no exported route would land the user on 404.html.
 */
const POST_LOGIN_TARGETS: ReadonlySet<string> = new Set([
  ADMIN_PATH,
  USERS_PATH,
  DOCUMENT_SUBMISSION_PATH,
  SERVICE_AGREEMENT_PATH,
  PAYMENT_METHOD_PATH,
  PLANS_PATH,
]);

/**
 * BE targets that name a screen this app does not have.
 *
 * Deliberately EMPTY for `dashboard` / `admin-dashboard`. Both used to alias to
 * `/users`, and since the BE names `dashboard` for every org user, that alias
 * was the real reason an invited user landed on Settings · User & Role — a
 * screen their own sidebar hides when they lack the `users` grant. Dropping the
 * target instead lets `permittedLanding` answer, which is what ✅ E04-C11 asks
 * for. Restore an entry here only for a target that names a route this app
 * actually exports, or the user lands on 404.html.
 */
const TARGET_ALIASES: Readonly<Record<string, string>> = {};

/**
 * Validate a redirect target against the persona that would follow it, or null
 * when it cannot be trusted. The persona check is the important half: a target
 * pointing into the staff console is exactly what looped a tenant admin between
 * '/' and '/admin', and staff must not be dropped into a tenant screen either.
 */
/**
 * `true` when `raw` names a real screen a post-login target may point at.
 *
 * `/no-access` deliberately fails this. It is a STATE, not a destination: it
 * means "we do not know what this user may open yet", which is true of one page
 * load and false of the next. Storing it would make that moment sticky, and
 * `app/signup/page.tsx` does exactly that — it stores whatever `defaultLanding`
 * returned. Keeping it out of the storable set is the one place that cannot be
 * bypassed by a caller.
 */
export function isStorableTarget(raw: string | null | undefined): boolean {
  if (!raw) return false;
  const path = normalizePath(raw);
  return POST_LOGIN_TARGETS.has(TARGET_ALIASES[path] ?? path);
}

export function normalizeTarget(raw: string | null | undefined, role: string | undefined) {
  if (!raw) return null;
  const path = normalizePath(raw);
  const target = TARGET_ALIASES[path] ?? path;
  if (!POST_LOGIN_TARGETS.has(target)) return null;
  const staff = role === SUPER_ADMIN_ROLE;
  if (isAdminRoute(target) !== staff) return null;
  return target;
}

/* -------------------------------------------------------------------------- */
/* Post-login target storage                                                   */
/* -------------------------------------------------------------------------- */

const REDIRECT_KEY = 'westmont.post_login_redirect';

type StoredRedirect = { userId: string; target: string };

/**
 * Cache the BE-owned role→view target (E04 US-16 AC4) that login/verify
 * returned, so an already-authenticated visitor lands back in the same place.
 * `GET /auth/me` does not echo it, so this is the only way the FE can learn it.
 *
 * It is stored WITH the user id it belongs to. Since 2026-08-20 the session
 * cookie is named per host, so one storage bucket now faces one cookie and two
 * ORGS can no longer share an entry. The owner check still earns its place on
 * the two paths that do share a bucket: two users of the SAME org signing in on
 * the same host, and single-origin dev, where one origin serves every identity.
 * Without it a target written by one account is read back by the next — the
 * stale `/admin` from a Super Admin sign-in is what looped a tenant admin on
 * 2026-07-29.
 */
export function rememberPostLoginRedirect(userId: string, target: string): void {
  if (typeof window === 'undefined') return;
  // A target that is not a real screen is not cached, and it clears whatever was
  // cached before rather than leaving the previous sign-in's entry to be
  // replayed. This is the guard that stops `/no-access` from round-tripping
  // through storage: `recallPostLoginRedirect` already rejects it on the way
  // out, but writing it at all makes one bad moment survive every later load.
  if (!isStorableTarget(target)) {
    forgetPostLoginRedirect();
    return;
  }
  try {
    const value: StoredRedirect = { userId, target: normalizePath(target) };
    window.localStorage.setItem(REDIRECT_KEY, JSON.stringify(value));
  } catch {
    // storage disabled (private mode): the persona default is used instead.
  }
}

/**
 * The stored target when it belongs to THIS user and is legal for their role,
 * else null. A value that fails any check is deleted on the spot, so a bad
 * entry cannot outlive one page load.
 */
export function recallPostLoginRedirect(userId: string, role: string | undefined): string | null {
  if (typeof window === 'undefined') return null;
  let stored: StoredRedirect | null = null;
  try {
    const raw = window.localStorage.getItem(REDIRECT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredRedirect).userId === 'string' &&
      typeof (parsed as StoredRedirect).target === 'string'
    ) {
      stored = parsed as StoredRedirect;
    }
  } catch {
    // Unreadable storage, or the pre-2026-07-29 plain-string format.
    stored = null;
  }
  const target = stored?.userId === userId ? normalizeTarget(stored.target, role) : null;
  if (!target) forgetPostLoginRedirect();
  return target;
}

/** Drop the stored target. Call on sign-out and whenever a session ends. */
export function forgetPostLoginRedirect(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(REDIRECT_KEY);
  } catch {
    // nothing to clean up when storage is unavailable.
  }
}

/* -------------------------------------------------------------------------- */
/* Tenant lifecycle gate                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The ONE screen an organization must clear before it can use the app, or null
 * when it is free to browse. Ordered by how early the step sits in onboarding.
 *
 * `/document-submission` renders its own three variants off the request status
 * (submit / submitted / update after rejection), so all three lifecycle states
 * point at the same route.
 */
export function lifecycleGate(
  access: AccessState | undefined,
  role?: string | undefined,
): string | null {
  const gate = onboardingGate(access);
  // WEST-782 — the subscription gate is a screen only an Org Admin can act on.
  // Everyone else gets told to contact them. An unknown role (session still
  // resolving) keeps the admin answer: `resolveRoute` never reaches the gate
  // before `/auth/me` settles, so this only covers direct callers.
  if (gate === PLANS_PATH && role !== undefined && role !== ORG_ADMIN_ROLE) {
    return SUBSCRIPTION_INACTIVE_PATH;
  }
  return gate;
}

function onboardingGate(access: AccessState | undefined): string | null {
  if (!access) return null;
  switch (access.status) {
    case 'pending_verification':
    case 'pending_review':
    case 'rejected':
      return DOCUMENT_SUBMISSION_PATH;
    // US-02b — documents are in, the signed agreement is not. The org waits on
    // the document-submission notice, NOT on the upload screen: the signed copy
    // is uploaded from the link in the "Sign your Westmont service agreement"
    // email, never straight off the submit button (user feedback 2026-08-03).
    // `isGateException` keeps that email link reachable.
    case 'pending_agreement':
      return DOCUMENT_SUBMISSION_PATH;
    case 'pending_payment_method':
      // Grandfathered orgs carry card_on_file (migration 0016) and pass through.
      return access.requires_payment_method ? PAYMENT_METHOD_PATH : null;
    case 'trial_expired':
      return access.subscription_active ? null : PLANS_PATH;
    default:
      // AC6 catch-all: `restricted` is BE-owned and may cover a state this map
      // does not name yet. Subscribing is the only way out of it.
      return access.restricted ? PLANS_PATH : null;
  }
}

/**
 * Screens a gated org may open even though the gate names a different one.
 *
 * Only one exists: `/service-agreement` while `pending_agreement`. The gate
 * parks the org on the document-submission notice ("check your email"), so
 * without this the email's "Upload signed agreement" link would bounce right
 * back to that notice and the agreement could never be uploaded at all.
 */
function isGateException(path: string, access: AccessState | undefined): boolean {
  return access?.status === 'pending_agreement' && isWithin(path, SERVICE_AGREEMENT_PATH);
}

/* -------------------------------------------------------------------------- */
/* The decision                                                                */
/* -------------------------------------------------------------------------- */

export type RouteDecision =
  | { kind: 'render' }
  /** Hold the frame: the answer depends on a request still in flight. */
  | { kind: 'wait' }
  | { kind: 'redirect'; to: string; reason: string }
  /** No usable session: clear client state and go to /login. */
  | { kind: 'signin'; reason: string };

export type QueryStatus = 'idle' | 'pending' | 'error' | 'success';

export type RouteInput = {
  pathname: string;
  /** `['auth','me']` status. 'error' means settled-401, never mid-refetch. */
  sessionStatus: QueryStatus;
  role?: string;
  /** `['organizations','access-state']` status; 'idle' when not requested. */
  accessStatus: QueryStatus;
  access?: AccessState;
  /** Already validated by `recallPostLoginRedirect`. */
  storedTarget: string | null;
  /**
   * The first screen this user's permissions actually open (✅ E04-C11), or null
   * when the set has not loaded or grants nothing. Computed by the caller from
   * the same nav list the sidebar renders (`components/layout/nav-items.ts`), so
   * the landing and the sidebar can never disagree.
   */
  permittedLanding?: string | null;
  /**
   * `['me','permissions']` status; 'idle' when the query is not requested
   * (signed out, or staff, who have no tenant modules).
   */
  permissionsStatus?: QueryStatus;
  /**
   * US-02b: the visitor arrived with an agreement-link token (⚠ E01-C42).
   * Read once at mount from the URL fragment / sessionStorage.
   */
  hasAgreementToken?: boolean;
  /**
   * The browser is on the Super Admin console host (user decision 2026-08-04).
   * That host serves the same bundle, so the tenant routes technically exist on
   * it; staff belong in `/admin/*` there and nowhere else. False on dev and
   * single-origin deploys, where one host serves both consoles.
   */
  isAdminHost?: boolean;
};

/**
 * Where the browser belongs, given everything currently known. Pure: same input,
 * same answer — which is what makes the fixed-point invariant testable.
 *
 * Rule order (first match wins):
 *   0. agreement link token           → render (the token is the credential)
 *   1. session still resolving        → wait (public routes render immediately)
 *   2. no session                     → render if public, else sign in
 *   3. staff                          → /admin from '/' or a landing page; else render
 *   4. signed in on /login, /signup   → the gate, or this user's landing
 *   5. other public routes            → render
 *   6. access state still resolving   → wait only where it changes the answer
 *   7. tenant inside the staff console→ the gate, or this user's landing
 *   8. lifecycle gate not satisfied   → the gate screen
 *  8b. onboarding step, gate cleared  → this user's landing
 *   9. '/'                            → the gate, or this user's landing
 *  9b. '/no-access'                   → this user's landing, else render
 *  10. anything else                  → render
 */
export function resolveRoute(input: RouteInput): RouteDecision {
  const path = normalizePath(input.pathname);
  const isPublic = isPublicRoute(path);

  // 0. US-02b (⚠ E01-C42): on the agreement screen the LINK TOKEN is the
  // credential, so neither the session rules nor the lifecycle gate may speak.
  // The reader is signed out on the apex, and may even be carrying a cookie for
  // a different org; the BE decides which org from the token, never the cookie.
  // Returning `render` also keeps the `signin` effect from ending that
  // unrelated session. See `@/lib/agreement-token`.
  if (input.hasAgreementToken && isWithin(path, SERVICE_AGREEMENT_PATH)) {
    return { kind: 'render' };
  }

  // 1–2. Session first: nothing else can be decided without it.
  if (input.sessionStatus === 'pending' || input.sessionStatus === 'idle') {
    return isPublic ? { kind: 'render' } : { kind: 'wait' };
  }
  if (input.sessionStatus === 'error') {
    return isPublic ? { kind: 'render' } : { kind: 'signin', reason: 'no session' };
  }

  // 3. Staff are not tenants: no lifecycle gate applies, and their console is
  // the only landing they have. On the dedicated console host (2026-08-04) that
  // is literal — the tenant routes are still in the bundle served there, and a
  // signed-in staff member has no business on any of them.
  if (input.role === SUPER_ADMIN_ROLE) {
    if (path === '/' || isAuthLandingRoute(path) || (input.isAdminHost && !isAdminRoute(path))) {
      return { kind: 'redirect', to: ADMIN_PATH, reason: 'staff landing' };
    }
    return { kind: 'render' };
  }

  // 6. The gate decides where a tenant may go, so a decision that could name a
  // destination has to wait for it. Elsewhere the page renders now and the gate
  // applies the moment the answer lands (one hop, never a loop).
  // `/no-access` is a decision point, not a page, exactly like '/': anyone who
  // lands there before their permission set resolved must be forwarded once it
  // does, or a user with modules would sit on a "no access" screen for ever.
  // `/subscription-inactive` too (WEST-782): it is only true while the gate names
  // it, so leaving it once the org pays again needs the same answer.
  const needsGate =
    path === '/' ||
    path === NO_ACCESS_PATH ||
    path === SUBSCRIPTION_INACTIVE_PATH ||
    isAdminRoute(path) ||
    isAuthLandingRoute(path);
  if (needsGate && (input.accessStatus === 'pending' || input.accessStatus === 'idle')) {
    return { kind: 'wait' };
  }
  // A failed access-state (403/404) leaves `gate` null: fail open, exactly as
  // the app behaved before the gate existed.
  const gate = lifecycleGate(input.access, input.role);

  // ✅ E04-C11 — no fixed role→redirect table. The BE's own target wins when it
  // named a real screen; otherwise the first module this user may open; only
  // then the persona default.
  //
  // The permission set has SPOKEN once its query settled, success or failure.
  // 'success' with no module is a fact about this user ("your grants open
  // nothing"); 'error' is a fact too, and `/no-access` says so in its own words
  // and offers a retry and a sign-out. 'pending', 'idle' (never asked) and an
  // absent status are NOT facts, and answering them with `/no-access` is how an
  // org_admin with a full catalogue gets dropped on a "no access" screen the
  // moment nothing else happens to name a target for them.
  const permissionsResolved =
    input.permissionsStatus === 'success' || input.permissionsStatus === 'error';

  // WEST-657 — a stored target that names an onboarding step is only a
  // destination while the gate still names that step. `app/signup/page.tsx`
  // writes `/document-submission` the moment the OTP is verified, and nothing
  // clears it when the org finishes onboarding: left in place it is the exact
  // redirect loop of WEST-499 (`docs/status.md:1454`), since rule 8b below
  // would move the org off the screen and this target would name it right back.
  // Only when onboarding is KNOWN to be over, on the same condition as rule 8b:
  // while the gate still names the step the target agrees with it and rule 8
  // speaks anyway, and an unreadable access-state must not cost a mid-onboarding
  // org the target that takes it back to its upload screen.
  const storedTarget =
    input.storedTarget && input.access && !gate && isOnboardingStepRoute(input.storedTarget)
      ? null
      : input.storedTarget;
  const knownLanding =
    storedTarget ?? input.permittedLanding ?? defaultLanding(input.role, permissionsResolved);

  // 6b. Landing follows permission (✅ E04-C11), so a decision that would NAME a
  // landing must not guess before the set lands: sending the user to `/users`
  // and then on to their real first module is two hops they watch happen. Only
  // when nothing else already decides the destination — a stored target wins
  // outright, and a lifecycle gate outranks any landing.
  if (needsGate && !gate && knownLanding === null) {
    return { kind: 'wait' };
  }

  // Every read of `landing` below sits on a `needsGate` path, and the guard
  // above already held the frame there when it is unknown, so the fallback is
  // unreachable. It is written out rather than asserted non-null.
  const landing = knownLanding ?? NO_ACCESS_PATH;

  // 4–5. Public pages: an authenticated visitor to /login or /signup goes into
  // the app; /forgot-password stays reachable with a session.
  if (isPublic) {
    if (isAuthLandingRoute(path)) {
      return { kind: 'redirect', to: gate ?? landing, reason: 'already signed in' };
    }
    return { kind: 'render' };
  }

  // 7. A tenant user inside /admin/*. Sent to a real screen — never to '/',
  // which would bounce straight back here.
  if (isAdminRoute(path)) {
    return { kind: 'redirect', to: gate ?? landing, reason: 'not staff' };
  }

  // 8. The org has an unfinished step; only that screen (and its sub-routes,
  // plus the one exception above) is reachable until it clears.
  if (gate && !isWithin(path, gate) && !isGateException(path, input.access)) {
    return { kind: 'redirect', to: gate, reason: 'lifecycle gate' };
  }

  // 8b. WEST-657 — the onboarding step is over. An org whose gate is clear has
  // added its card (or is past that entirely), so the notice screen has nothing
  // true left to say to it: it can only repeat "your 7-day trial begins once
  // you add a payment method" at an org already trialing or paying. The rule
  // lives HERE and not in the page, because the page is not allowed to
  // navigate (WEST-499: doing it there both broke the single routing authority
  // and looped).
  //
  // `input.access` is the condition, not `!gate` alone: access-state can answer
  // 403/500, and that leaves `gate` null too. The app fails open there, so the
  // page keeps rendering and its own `approved` branch still has an audience.
  //
  // Destination is the standing pattern of rules 4, 7 and 9 — this app has no
  // '/dashboard' route, and ✅ E04-C11 makes the landing permission-driven. The
  // gate is null by here, so `gate ?? landing` would only ever read `landing`;
  // it is spelled out instead. `wait` while the landing is unknown, for the
  // same reason as rule 6b: naming one before the permission set lands is how
  // an org_admin gets parked on /no-access.
  if (input.access && !gate && isOnboardingStepRoute(path)) {
    if (knownLanding === null) return { kind: 'wait' };
    return { kind: 'redirect', to: knownLanding, reason: 'onboarding complete' };
  }

  // 8c. WEST-782 — the "contact your admin" screen has no way out of its own, so
  // the router is its exit: once the gate no longer names it (the org paid, or
  // this user is the admin) the user goes to their landing. An unreadable
  // access-state leaves the user where they are, like rule 8b.
  if (path === SUBSCRIPTION_INACTIVE_PATH && input.access && gate !== SUBSCRIPTION_INACTIVE_PATH) {
    return { kind: 'redirect', to: gate ?? landing, reason: 'subscription restored' };
  }

  // 9. '/' is a router, not a page.
  if (path === '/') {
    return { kind: 'redirect', to: gate ?? landing, reason: 'root landing' };
  }

  // 9b. The no-access screen is only for a user whose landing really is nothing.
  // The permission set has resolved by here (rule 6b held the frame otherwise),
  // so a landing that names a screen means they have one and belong on it.
  if (path === NO_ACCESS_PATH) {
    return landing === NO_ACCESS_PATH
      ? { kind: 'render' }
      : { kind: 'redirect', to: landing, reason: 'permitted landing' };
  }

  return { kind: 'render' };
}
